import persistentStore from '../persistentStore'

const orderService = (baseUrl, endpoint) => ({
  /**
   * Creates an order service with methods to interact with the order API.
   *
   * @param {string} baseUrl - The base URL of the API.
   * @param {string} endpoint - The endpoint for the order service.
   * @returns {Object} An object containing methods for the order service.
   * @property {Function} orderBilling - Fetches billing information for a specific order.
   *
   * @typedef {Object} OrderBillingParams
   * @property {AbortSignal} signal - An AbortSignal to allow aborting the request.
   * @property {string} id - The ID of the order (required).
   *
   * @method orderBilling
   * @param {OrderBillingParams} params - Parameters for the order billing request.
   * @throws {Error} Throws an error if the `id` parameter is not provided.
   * @returns {Promise<string|Error>} A promise that resolves to the response data as a string, or an error if the request fails.
   */
  orderBilling: async ({ signal, id = undefined }) => {
    if (typeof id == 'undefined') {
      throw new Error('The id is required')
    }

    try {
      const response = await fetch(
        `${baseUrl}/${endpoint}/shopify/${id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            authorization: persistentStore.getToken(),
            app: process.env.REACT_APP_APPLICATION_NAME,
            'api-key': process.env.REACT_APP_ORDER_API_KEY
          },
          signal
        }
      )

      const data = await response.text()
      return data
    } catch (error) {
      return error
    }
  }
})

export default orderService(process.env.REACT_APP_API_ORDER_PATH, 'orders')
